// The shell's entry component: build the kernel once, register the built-ins, hand it to the
// desktop. Everything below this is either kernel, host or skin (§3.1).
import { useEffect, useState } from 'react';
import { BUILTIN_APPS } from '@/apps';
import { createConnectionsApp } from '@/apps/connections';
import { createChatApp } from '@/apps/chat';
import { createAlarmsApp } from '@/apps/alarms';
import { createRemindersApp } from '@/apps/reminders';
import { redeemConnectLink } from '@/host/connect-link';
import { createExplorerApp } from '@/apps/explorer';
import { createInspectorApp } from '@/apps/inspector';
import { createSettingsApp } from '@/apps/settings';
import { createStoreApp } from '@/apps/store';
import { createCascadeApp } from '@/apps/cascade';
import { createFreeCellApp } from '@/apps/freecell';
import { createPyramidApp } from '@/apps/pyramid';
import { createConquestApp } from '@/apps/conquest';
import { createInterceptApp } from '@/apps/intercept';
import { createChessApp } from '@/apps/chess';
import { createBackgammonApp } from '@/apps/backgammon';
import { createPhalanxApp } from '@/apps/phalanx';
import { createRivetApp } from '@/apps/rivet';
import { createBurrowApp } from '@/apps/burrow';
import { createVinesApp } from '@/apps/vines';
import { createSortieApp } from '@/apps/sortie';
import { createPulseApp } from '@/apps/pulse';
import { createTimberApp } from '@/apps/timber';
import { createClockApp } from '@/apps/clock';
import { createConsoleApp } from '@/apps/console';
import { createArchiveApp } from '@/apps/archive';
// ── §34.6 (M60) — Media ────────────────────────────────────────────────────────────────────
import { createMediaApp } from '@/apps/media';
// §34.3 / §34.4 / §34.5 — Workbench lane B (M57–M59).
import { createNotepadApp } from '@/apps/notepad';
import { createNotesApp } from '@/apps/notes';
import { createDiffApp } from '@/apps/diff';
// ── Lane C — §34.9 Voice · §34.10 Snip · §34.12 Weather (M62–M65).
import { createVoiceApp } from '@/apps/voice';
import { createSnipApp } from '@/apps/snip';
import { createWeatherApp } from '@/apps/weather';
import { createPdfApp } from '@/apps/pdf';
import { createRecycleBinApp } from '@/apps/recycle-bin';
import { createPhotosApp } from '@/apps/photos';
import { createTerminalApp } from '@/apps/terminal';
import { createHangarKernel, type HangarKernel } from '@/kernel';
import { KernelProvider } from '@/kernel/react';
import { Desktop } from '@/host/Desktop';
import {
  createExplorerAccess,
  createInspectorAccess,
  createSettingsAccess,
  createClockAccess,
  createConsoleAccess,
  createArchiveAccess,
  createNotepadAccess,
  createNotesAccess,
  createVoiceAccess,
  createSnipAccess,
  createWeatherAccess,
  createPdfAccess,
  createRecycleBinAccess,
  createPhotosAccess,
  createStoreAccess,
  createArcadeAccess,
  createPlayAccess,
  createSocialAccess,
  createChatAccess,
  createAlarmAccess,
  createRemindersAccess,
  createMediaAccess,
  createTerminalAccess,
} from '@/host/capabilities';
import { installDebugBridge } from '@/host/debug';
import { installErrorLog } from '@/host/errorlog';
import { SkinProvider, useSkin } from '@/host/SkinProvider';
import { beginSignIn, createServerAccess, decideAccess } from '@/kernel/server';
import { markStorageSeen, wasStorageCleared } from '@/kernel/vfs/eviction';
import { userRoot } from '@hangar/shared';
import { saveBlob } from '@/host/download';
import { COPY } from '@/copy';
import { SIGNIN_ERROR_PARAM } from '@/kernel/server';

export const HANGAR_VERSION = '0.1.0';

function buildKernel(): HangarKernel {
  const kernel = createHangarKernel();
  for (const app of BUILTIN_APPS) kernel.apps.register(app);
  // D-059 — the built-ins §11 grants a capability to, and the only place that list exists.
  kernel.apps.register(createSettingsApp(createSettingsAccess(kernel, HANGAR_VERSION)));
  kernel.apps.register(createExplorerApp(createExplorerAccess(kernel)));
  kernel.apps.register(createInspectorApp(createInspectorAccess(kernel)));
  kernel.apps.register(createStoreApp(createStoreAccess(kernel)));
  kernel.apps.register(createPhotosApp(createPhotosAccess(kernel)));
  kernel.apps.register(createPdfApp(createPdfAccess(kernel)));
  // §34.2 (D-509) — Archive writes beside the archive it was opened on, which §9.5's file grant
  // cannot express. Lazy like the rest of Workbench (D-511).
  kernel.apps.register(createArchiveApp(createArchiveAccess(kernel)));
  // §34.6 (D-520) — Media reads §15's global mute and nothing else. Lazy like the rest of
  // Workbench (D-511); a factory because it holds a capability, not because it is lazy.
  kernel.apps.register(createMediaApp(createMediaAccess(kernel)));
  // ── §34 Workbench, lane B (M57–M59) ─────────────────────────────────────────────────────
  // §34.3 (D-541) — Notepad joins the holders for its markdown preview: a §14 setting, and the
  // images a document points at. Eager, unlike the wave's own apps — it opens six file types.
  kernel.apps.register(createNotepadApp(createNotepadAccess(kernel)));
  // §34.4 (D-545) — Quick Notes arms its global capture chord as it registers, so the card summons
  // from any app before this one has ever been opened. Lazy like the rest of Workbench (D-511).
  kernel.apps.register(createNotesApp(createNotesAccess(kernel)));
  // §34.5 — Diff holds no capability (§34.1's default): two texts in, two views out. Registered here
  // beside the rest of lane B so the wave's apps are one block rather than three places.
  kernel.apps.register(createDiffApp());
  kernel.apps.register(createClockApp(createClockAccess(kernel)));
  // ── Lane C — §34.9/§34.10/§34.12 (M62–M65). All three are lazy stubs (D-511) and all three hold
  // a D-059 capability, which is why they are here rather than in `apps/index.ts`.
  //
  // §34.9 — Voice Memos. Its capability answers *which* directory memos live in; the reach itself is
  // §9.5's `extraRoots`, named for `voice` in `kernel/index.ts` and for nothing else (D-560).
  kernel.apps.register(createVoiceApp(createVoiceAccess(kernel)));
  // §34.10 (D-494) — Snip. Its capability *is* the mechanism: tab capture, the picker overlay, the
  // window manager's geometry and §15's shutter. Granted to `snip` and to nothing else.
  kernel.apps.register(createSnipApp(createSnipAccess(kernel)));
  // §34.12 (D-497) — Weather, the wave's **only** gated app. `requiresAuth` is on the manifest and
  // §24.11's `visible()` is what enforces it: registration cannot, because `buildKernel()` runs before
  // identity is known. Its capability reads two §14 settings and calls §34.8's route — never a URL.
  kernel.apps.register(createWeatherApp(createWeatherAccess(kernel)));
  // §25.4 — Cascade reads two §14 numbers (DAS/ARR) and nothing else.
  kernel.apps.register(createCascadeApp(createArcadeAccess(kernel)));
  // §29.2 (D-380) — the card titles hold the same capability for one call: `today()`, the UTC date
  // their daily deal is seeded from. A game may not read a clock (§24.4), so the shell reads it.
  kernel.apps.register(createFreeCellApp(createArcadeAccess(kernel)));
  kernel.apps.register(createPyramidApp(createArcadeAccess(kernel)));
  // §26.7 (D-210) — the two titles multiplayer covers. They get a play session and nothing else:
  // no roster, no presence, no invite link. One capability, granted twice, on two lines.
  const play = createPlayAccess(kernel);
  kernel.apps.register(createConquestApp(play));
  kernel.apps.register(createInterceptApp(play));
  // §30.1 — the allowlist grows by three in wave 2; Chess is the first.
  kernel.apps.register(createChessApp(play));
  kernel.apps.register(createBackgammonApp(play));
  kernel.apps.register(createPhalanxApp());
  kernel.apps.register(createRivetApp());
  kernel.apps.register(createBurrowApp());
  kernel.apps.register(createVinesApp());
  kernel.apps.register(createSortieApp(play));
  kernel.apps.register(createPulseApp());
  // §32 — Timber, the Arcade's first Puzzle. Single-player and capability-free, like the clones.
  kernel.apps.register(createTimberApp());
  kernel.apps.register(createConsoleApp(createConsoleAccess(kernel)));
  // §26.3 — the roster. Gated, so `apps.visible()` keeps it off a guest's desktop (§24.11).
  kernel.apps.register(createConnectionsApp(createSocialAccess(kernel)));
  // §26.5 — Chat. Gated too, and registered beside the roster because they are two views of the
  // same list of people.
  kernel.apps.register(createChatApp(createChatAccess(kernel)));
  // §27 — shared reminder lists. Gated: every list is on the server and every share is a §26
  // connection, so a guest has nothing to show and nowhere to put anything.
  kernel.apps.register(createRemindersApp(createRemindersAccess(kernel)));
  // §11.14 — Alarms & Stopwatch. Ungated: a stopwatch does not need an account, and a guest's
  // schedule persists to their own browser exactly as their files do.
  kernel.apps.register(createAlarmsApp(createAlarmAccess(kernel)));
  kernel.apps.register(createRecycleBinApp(createRecycleBinAccess(kernel)));
  kernel.apps.register(
    createTerminalApp({
      access: createTerminalAccess(kernel),
      // §11.2 — installed apps that declare commands, exposed to the terminal as providers in
      // install order. One adapter, so the terminal never learns what a bridge is (M13.2).
      providers: () =>
        kernel.exec.commandsByApp().map((provider) => ({
          id: provider.appId,
          commands: provider.commands,
          run: (cmd, args, cwd) => kernel.exec.run({ cmd, args, cwd }),
        })),
    }),
  );
  kernel.registry.start();
  return kernel;
}

/**
 * §8.3 — the whole desktop, or the skin's `LoginScreen` and nothing else.
 *
 * `LoginScreen` is a skin component, so the provider still mounts; what does not mount is the
 * `Desktop`, which is where every window, bar and overlay comes from. "Mounts nothing else" is
 * a statement about the shell, and this is the line it is drawn on.
 */
function LockedOut() {
  const { LoginScreen } = useSkin().components;
  return <LoginScreen />;
}

export function App() {
  const [kernel] = useState(buildKernel);
  const [ready, setReady] = useState(false);
  /** §8.3 — `null` until the server has been asked; a guest build settles on `false` at once. */
  const [locked, setLocked] = useState<boolean | null>(null);
  /**
   * Whether there is a server to sign in *to*. §18 F2's "sign-in fails" needs somewhere to fail:
   * navigating to `/auth/login` in a build with nothing behind `/api` would leave the desktop for
   * a dead page, which is worse than the toast that says so.
   */
  const [hasServer, setHasServer] = useState(false);

  useEffect(() => {
    installDebugBridge(kernel, HANGAR_VERSION);
    // D-191 — the half of the Console's shell log the bus cannot supply.
    const stopErrorLog = installErrorLog(kernel);
    let cancelled = false;

    // §18 F2 — coming back from a sign-in that could not happen. `/auth/login` redirects here
    // with the reason rather than to a page nobody can see (D-151), and PLAN's string lives in
    // `copy.ts` where all user-facing copy does.
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has(SIGNIN_ERROR_PARAM)) {
      kernel.shellui.showToast(COPY.signInUnavailable);
      // Cleared from the URL so a reload does not raise it again — the toast is about what just
      // happened, not a property of the page.
      window.history.replaceState(null, '', window.location.pathname);
    }

    // §7.4 step 4 — the triggers: on load, on `online`, every 60 s while visible, and 2 s after
    // the last local write. Attached always; it does nothing until a session enables it.
    const detachSync = kernel.sync.attach();

    // §9.3 — one `message` listener for every app frame, attached before any frame can exist.
    const detachBridge = kernel.bridge.attach(window);

    void (async () => {
      // §8.3 — asked once, before the desktop is shown. F1 keeps this from being a gate: an
      // unreachable server answers `locked: false`, so an offline desktop still boots (D-161).
      //
      // **Everything else waits on it** (D-173): §7.1 roots the tree at the *user's* space, and a
      // hydrate that started first would bootstrap `/Users/guest` for somebody who is signed in.
      try {
        const decision = await decideAccess(createServerAccess());
        if (cancelled) return;
        kernel.setIdentity(
          decision.me === null
            ? null
            : {
                id: decision.me.user.id,
                displayName: decision.me.user.displayName,
                isAdmin: decision.me.isAdmin,
              },
          // §26.1 (D-316) — the instance comes from the server or it does not come at all. A
          // client that guessed would namespace every ref under a name nothing else uses.
          decision.me?.instance ?? null,
        );
        setLocked(decision.locked);
        // §26.3 — a `#/connect/<token>` link, redeemed once identity is known. After
        // `setIdentity`, because the signed-out branch depends on it and running earlier would
        // send a signed-in person through a sign-in they do not need. Never awaited into the boot
        // path: an invitation that cannot be redeemed must not be a desktop that will not start.
        void redeemConnectLink({
          readHash: () => window.location.hash,
          clearHash: () =>
            window.history.replaceState(null, '', window.location.pathname + window.location.search),
          store: window.sessionStorage,
          signedIn: () => decision.signedIn,
          accept: (token) => kernel.social.accept(token),
          notify: (message) => kernel.shellui.showToast(message),
          copy: {
            accepted: COPY.social.connected,
            failed: COPY.social.connectFailed,
            pending: COPY.social.connectPending,
          },
        });
        setHasServer(decision.config !== null);
        // §7.4 — "Guests never sync." Only a recognised session turns the client on.
        if (decision.signedIn) void kernel.startSync();
      } catch {
        if (!cancelled) setLocked(false);
      }

      // §5.6 — settings first (the skin decides what the desktop looks like), then the installed
      // apps (a restored window may belong to one), then the session. **Restore never blocks the
      // desktop**: a session that will not load is no session at all, and the boot continues.
      try {
        await kernel.settings.hydrate();
        await kernel.installed.hydrate();
        // §9.6 — the ten-minute `HEAD` schedule, started once the installed set is known. It
        // probes anything it has not seen, so an install is checked the moment it lands.
        await kernel.health.start();
        const saved = await kernel.session.read();
        if (!cancelled && saved) kernel.session.restore(saved);
      } catch (error: unknown) {
        console.warn('session restore failed', error);
      } finally {
        if (!cancelled) setReady(true);
      }

      // §18 F5 — was this browser cleared between sessions? Asked after the tree has been
      // bootstrapped, so "empty" means empty of the user's own files rather than not yet built.
      // Once per session and once ever: the setting is what makes the second visit quiet.
      try {
        const scope = typeof localStorage === 'undefined' ? undefined : localStorage;
        const home = userRoot(kernel.vfs.currentUser());
        const entries = await kernel.vfs.list(home);
        const hadFiles = (
          await Promise.all(
            entries
              .filter((entry) => entry.isDir && !entry.name.startsWith('.'))
              .map(async (entry) => (await kernel.vfs.list(entry.path)).length > 0),
          )
        ).some(Boolean);

        if (!cancelled && wasStorageCleared(scope, hadFiles) && !kernel.settings.get('ui.evictionNoticeShown')) {
          kernel.settings.set('ui.evictionNoticeShown', true);
          kernel.shellui.showToast(COPY.storageCleared);
        }
        markStorageSeen(scope, hadFiles);
      } catch (error: unknown) {
        console.warn('storage check', error);
      }


      // §11.14 — the alarm schedule, read from disk and armed.
      //
      // **After** the tree is bootstrapped, because it reads a file, and after `setIdentity`,
      // because a guest's `/Apps` is a different space from a signed-in user's. Anything that came
      // due while the tab was shut rings here, as soon as there is a desktop to ring on.
      try {
        if (!cancelled) {
          await kernel.alarms.hydrate();
          kernel.alarms.start();
        }
      } catch (error: unknown) {
        console.warn('alarms', error);
      }

      // §34.11 (M64.4) — the calendar's reminders, armed the same way and for the same reasons: it
      // reads a file, so the tree has to be up, and a guest's `/Apps` is not a signed-in user's.
      //
      // It holds no schedule of its own — every instant is derived from `/Apps/clock/calendar.json` on
      // each check — so there is nothing here that could disagree with the events the user can see.
      // Anything that came due while the tab was shut is announced here, late, as D-335 requires.
      try {
        if (!cancelled) {
          await kernel.calendarReminders.hydrate();
          kernel.calendarReminders.start();
        }
      } catch (error: unknown) {
        console.warn('calendar reminders', error);
      }

      // §11.7 (D-285) — an arranged desktop gets an icon for any app it has never been offered,
      // so an update that ships a new built-in does not leave it behind.
      //
      // It writes **only when there is something new**, which is what keeps D-284's promise that
      // boot does no filesystem work in the steady state: the usual answer here is "nothing to
      // do". A desktop that is still virtual returns immediately — it shows every app by
      // construction and cannot drift.
      try {
        if (!cancelled) await kernel.desktop.syncApps();
      } catch (error: unknown) {
        console.warn('desktop sync', error);
      }

      // §12 — the one expensive source, walked once. Deliberately **after** the desktop is
      // ready: a desktop must not wait on a filesystem walk to appear, and a launcher opened
      // before this lands simply finds no files yet. Everything after follows `fs:changed`.
      void kernel.reindexFiles().catch((error: unknown) => console.warn('search index', error));
    })();

    const detach = kernel.session.attach(typeof document === 'undefined' ? undefined : document);
    // §9.6 / F10 — the unload clock for `resumable` apps. It only ever watches; the frames it
    // decides about belong to the host layer.
    const detachResume = kernel.resume.attach();
    // §10.1 — the shell's own paste, which is the gesture that makes a clipboard *read* legal.
    const detachClipboard = kernel.clipboard.attach(document);
    return () => {
      cancelled = true;
      detach();
      detachResume();
      detachClipboard();
      stopErrorLog();
      detachBridge();
      detachSync();
      kernel.alarms.stop();
      kernel.health.stop();
    };
  }, [kernel]);

  if (!ready || locked === null) return <div data-testid="hangar-booting" />;

  // §8.3 — "the web client renders the skin's `LoginScreen` and mounts nothing else".
  if (locked) {
    return (
      <KernelProvider value={kernel}>
        <SkinProvider>
          <div data-testid="hangar-locked">
            <LockedOut />
          </div>
        </SkinProvider>
      </KernelProvider>
    );
  }

  return (
    <KernelProvider value={kernel}>
      <div data-testid="hangar-root">
        <Desktop
          onExport={() => {
            // §7.7 — the guest's escape hatch. It must work without an account.
            void kernel.exports.exportEverything().then((result) => {
              saveBlob(result.blob, kernel.exports.suggestedFilename());
            });
          }}
          onSignIn={() => {
            // §16 — a top-level navigation, because `/auth/login` answers with a 302 to the
            // issuer and a `fetch` would follow it into a page nobody can see. With no server
            // there is nothing to navigate *to*, and F2's string is the honest answer.
            if (!hasServer) {
              kernel.shellui.showToast(COPY.signInUnavailable);
              return;
            }
            beginSignIn(() => kernel.shellui.showToast(COPY.signInUnavailable));
          }}
        />
      </div>
    </KernelProvider>
  );
}
